import { useQuery } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';
import { useContext } from 'react';

import { config } from '../../../modules/config';
import { AuthContext } from '../../../modules/context/authContext';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type { ErrorEnvelop } from '../../../modules/hooks';
import type {
  DashboardReportPayload,
  DashboardView,
} from '../../../modules/interfaces/dashboard';
import { endpoints } from '../../../modules/mappers/urls';
import { toDashboardView } from '../../../modules/utils/dashboard';

type DashboardEnvelope = SuccessEnvelop<DashboardReportPayload>;

/**
 * Module-scope so its identity is stable — an inline lambda would make
 * react-query re-run `select` on every render.
 */
const selectDashboardView = (envelope: DashboardEnvelope): DashboardView =>
  toDashboardView(envelope.result);

export interface UseDashboardReportResult {
  data?: DashboardView;
  /** First load only — background refetches do not flip this. */
  loading: boolean;
  /** Any in-flight request, including a background refetch. */
  fetching: boolean;
  error: AxiosError<ErrorEnvelop> | null;
  /** False when there is no property or token — the query never fires. */
  enabled: boolean;
  refetch: () => void;
}

/**
 * Loads the consolidated property dashboard (`GET v1/reports/dashboard`).
 *
 * Built directly on `useQuery` rather than the shared `useQueryRequest`, on
 * purpose: that hook's `queryKey` is `[url, params]`, which omits the property,
 * and its `staleTime` is hardcoded to five minutes. Switching properties would
 * therefore keep showing the previous property's numbers, and the call site has
 * no way to fix it. Same axios escape hatch the lock/access-code hooks use.
 *
 * @param date ISO `YYYY-MM-DD`. Scopes the arrivals/departures slice only —
 *   occupancy is fixed server-side to the current calendar year and ratings are
 *   all-time.
 */
export const useDashboardReport = (date: string): UseDashboardReportResult => {
  const { credentialsInfo } = useContext(AuthContext) || {};
  const { selectedProperty } = useContext(UserPropertiesContext) || {};

  const token = credentialsInfo?.token;
  const propertyId = selectedProperty?.property?.uuid;
  const enabled = Boolean(token && propertyId);

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    DashboardEnvelope,
    AxiosError<ErrorEnvelop>,
    DashboardView
  >({
    queryKey: ['reports', 'dashboard', propertyId ?? null, date],
    queryFn: async ({ signal }) => {
      const response = await axios.request<DashboardEnvelope>({
        url: endpoints.DASHBOARD_REPORT,
        baseURL: config.bookingEngine.endpoint,
        method: 'get',
        params: { date },
        headers: {
          authorization: token as string,
          'property-id': propertyId as string,
        },
        signal,
      });

      return response.data;
    },
    select: selectDashboardView,
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    data,
    loading: isLoading,
    fetching: isFetching,
    error: error ?? null,
    enabled,
    refetch: () => {
      void refetch();
    },
  };
};
